import { Link } from "react-router-dom";
import { Zap, ArrowLeft } from "lucide-react";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      {/* Header */}
      <header className="border-b border-slate-700 bg-slate-900/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-indigo-500" />
            </div>
            <span className="text-xl font-bold text-indigo-500">ClientKit</span>
          </Link>
          <Link to="/" className="text-sm text-indigo-400 hover:text-indigo-500 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-indigo-500 mb-2">Terms of Service</h1>
        <p className="text-slate-400 mb-8">Last updated: January 23, 2026</p>

        <div className="prose prose-invert prose-slate max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using ClientKit ("Service"), operated at yourfreelanceros.com, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">2. Description of Service</h2>
            <p>
              ClientKit is a software-as-a-service (SaaS) platform designed to help freelancers, consultants, and service providers manage their business operations, including:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Client relationship management</li>
              <li>Project tracking and management</li>
              <li>Proposal and contract creation</li>
              <li>Email communication and scheduling</li>
              <li>AI-powered content generation</li>
              <li>Analytics and reporting</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">3. Account Registration</h2>
            <p>To use certain features of the Service, you must register for an account. You agree to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Keep your password secure and confidential</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">4. Subscription and Payment</h2>
            
            <h3 className="text-lg font-medium text-indigo-500 mt-4 mb-2">4.1 Plans and Pricing</h3>
            <p>
              The Service offers various subscription plans (Free, Pro, Growth) with different features and limitations. Pricing is displayed on our website and may be updated from time to time.
            </p>

            <h3 className="text-lg font-medium text-indigo-500 mt-4 mb-2">4.2 Billing</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Paid subscriptions are billed in advance on a monthly or annual basis</li>
              <li>All payments are processed securely through Stripe</li>
              <li>You authorize us to charge your payment method on file</li>
            </ul>

            <h3 className="text-lg font-medium text-indigo-500 mt-4 mb-2">4.3 Refunds</h3>
            <p>
              We offer refunds at our discretion. If you are unsatisfied with the Service, please contact us within 14 days of your purchase to request a refund.
            </p>

            <h3 className="text-lg font-medium text-indigo-500 mt-4 mb-2">4.4 Cancellation</h3>
            <p>
              You may cancel your subscription at any time. Upon cancellation, you will retain access until the end of your current billing period.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">5. Acceptable Use</h2>
            <p>You agree NOT to use the Service to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on intellectual property rights of others</li>
              <li>Send spam, phishing emails, or malicious content</li>
              <li>Harass, abuse, or harm another person or entity</li>
              <li>Impersonate any person or entity</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Use automated systems to access the Service without permission</li>
              <li>Collect or store personal data about other users without consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">6. User Content</h2>
            
            <h3 className="text-lg font-medium text-indigo-500 mt-4 mb-2">6.1 Ownership</h3>
            <p>
              You retain all rights to the content you create, upload, or store using the Service ("User Content"). This includes client data, proposals, contracts, and other materials.
            </p>

            <h3 className="text-lg font-medium text-indigo-500 mt-4 mb-2">6.2 License to Us</h3>
            <p>
              By using the Service, you grant us a limited license to host, store, and display your User Content solely for the purpose of providing the Service to you.
            </p>

            <h3 className="text-lg font-medium text-indigo-500 mt-4 mb-2">6.3 Responsibility</h3>
            <p>
              You are solely responsible for your User Content and ensuring you have the necessary rights to use and share it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">7. AI-Generated Content</h2>
            <p>
              The Service includes AI-powered features that generate content based on your inputs. You acknowledge that:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>AI-generated content may not always be accurate or appropriate</li>
              <li>You are responsible for reviewing and editing AI-generated content before use</li>
              <li>We do not guarantee the accuracy, legality, or suitability of AI-generated content</li>
              <li>AI features are provided "as is" without warranties</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">8. Third-Party Integrations</h2>
            <p>
              The Service integrates with third-party services (Google, Stripe, SendGrid). Your use of these integrations is subject to their respective terms of service and privacy policies. We are not responsible for the actions or policies of third-party services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">9. Intellectual Property</h2>
            <p>
              The Service, including its original content, features, and functionality, is owned by ClientKit and is protected by copyright, trademark, and other intellectual property laws. Our trademarks may not be used without prior written consent.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">10. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>MERCHANTABILITY</li>
              <li>FITNESS FOR A PARTICULAR PURPOSE</li>
              <li>NON-INFRINGEMENT</li>
              <li>ACCURACY OR COMPLETENESS OF CONTENT</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">11. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, CLIENTKIT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES, REGARDLESS OF THE CAUSE OF ACTION.
            </p>
            <p className="mt-2">
              Our total liability for any claims under these Terms shall not exceed the amount you paid us in the twelve (12) months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">12. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless ClientKit, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from your use of the Service or violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">13. Termination</h2>
            <p>
              We may terminate or suspend your account immediately, without prior notice, for any reason, including breach of these Terms. Upon termination:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Your right to use the Service will immediately cease</li>
              <li>We may delete your account and User Content</li>
              <li>Provisions that should survive termination will remain in effect</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">14. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will provide notice of significant changes by posting the updated Terms on the Service and updating the "Last updated" date. Your continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">15. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-indigo-500 mb-3">16. Contact Us</h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <ul className="list-none pl-0 mt-2">
              <li><strong>Email:</strong> hello@yourfreelanceros.com</li>
              <li><strong>Website:</strong> yourfreelanceros.com</li>
            </ul>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-700 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-400">© 2026 ClientKit. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-sm text-indigo-400 hover:text-indigo-500">Privacy Policy</Link>
            <Link to="/terms" className="text-sm text-indigo-400 hover:text-indigo-500">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
